/**
 * ClientLibraryLinkage — v3.12.0
 *
 * 2-way linkage between Client Profile cards (Certifications, Key Personnel,
 * Past Performance, Winning Proposals) and Pursuit Manage Documents.
 *
 * Exports:
 *   • <PullFromLibraryModal>      Pull a KB record + its files into the pursuit
 *   • <PromoteToLibraryModal>     Push a pursuit doc into the client library
 *   • <WinLibraryPromptDialog>    One-time prompt after Mark-as-Won to promote
 *                                 submitted docs to Winning Proposals
 *
 * Storage bridges GridFS (kb_attachments) ↔ raw bytes (pursuit_kb_files);
 * the heavy lifting is on the backend (pursuit_kb.py v3.12.0 routes).
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Award, Users, Briefcase, Trophy, Loader2, ArrowDownToLine,
  ArrowUpToLine, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ENTITY_TYPES = [
  { value: 'past-performances', label: 'Past Performance', shortLabel: 'Past Perf', icon: Briefcase, color: 'text-emerald-600', endpoint: 'past-performances' },
  { value: 'certifications', label: 'Certifications', shortLabel: 'Certs', icon: Award, color: 'text-amber-600', endpoint: 'certifications' },
  { value: 'key-personnel', label: 'Key Personnel', shortLabel: 'Personnel', icon: Users, color: 'text-blue-600', endpoint: 'key-personnel' },
  { value: 'winning-proposals', label: 'Winning Proposals', shortLabel: 'Proposals', icon: Trophy, color: 'text-yellow-600', endpoint: 'winning-proposals' },
];

const labelOf = (entityType, rec) => {
  if (entityType === 'past-performances') return rec.contract_title || 'Past Performance';
  if (entityType === 'certifications') return rec.name || 'Certification';
  if (entityType === 'key-personnel') {
    const fn = rec.first_name || ''; const ln = rec.last_name || '';
    return `${fn} ${ln}`.trim() || 'Team Member';
  }
  if (entityType === 'winning-proposals') return rec.title || 'Winning Proposal';
  return 'Record';
};

const subOf = (entityType, rec) => {
  if (entityType === 'past-performances') return rec.client_agency || '';
  if (entityType === 'certifications') return rec.issuing_body || rec.status || '';
  if (entityType === 'key-personnel') return rec.title || '';
  if (entityType === 'winning-proposals') return rec.agency || '';
  return '';
};

// ---------------------------------------------------------------------------
// Pull Modal
// ---------------------------------------------------------------------------

export function PullFromLibraryModal({
  open,
  onOpenChange,
  pursuitId,
  organizationId,  // optional — fallback for orgs without a pursuit context
  authToken,
  onPulled,
}) {
  const [activeType, setActiveType] = useState('past-performances');
  const [records, setRecords]       = useState({});   // { entity_type: [rows] }
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState({});   // { entity_type: { id: boolean } }
  const [pulling, setPulling]       = useState(false);
  const [pullProgress, setPullProgress] = useState({ done: 0, total: 0 });

  // Total items checked across all tabs
  const totalSelected = Object.values(selected).reduce(
    (sum, ids) => sum + Object.values(ids).filter(Boolean).length,
    0,
  );

  const selectedForTab = (entityType) =>
    Object.values(selected[entityType] || {}).filter(Boolean).length;

  const fetchRecords = async (entityType) => {
    if (!authToken) return;
    setLoading(true);
    try {
      const url = pursuitId
        ? `${API}/api/pursuit-kb/${pursuitId}/library/${entityType}`
        : `${API}/api/knowledge-base/${entityType}?organization_id=${organizationId}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${authToken}` } });
      if (res.ok) {
        const data = await res.json();
        setRecords((prev) => ({ ...prev, [entityType]: data.items || [] }));
      } else {
        setRecords((prev) => ({ ...prev, [entityType]: [] }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset state every time dialog opens; re-fetch when tab changes
  useEffect(() => {
    if (open) {
      setRecords({});
      setSelected({});
      setActiveType('past-performances');
    }
  }, [open]);

  useEffect(() => {
    if (open) fetchRecords(activeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeType, pursuitId, organizationId]);

  const toggleOne = (entityType, id) => {
    setSelected((prev) => {
      const cur = prev[entityType] || {};
      return { ...prev, [entityType]: { ...cur, [id]: !cur[id] } };
    });
  };

  const toggleAll = (entityType) => {
    const recs = records[entityType] || [];
    if (!recs.length) return;
    const cur = selected[entityType] || {};
    const allOn = recs.every((r) => cur[r.id]);
    const next = {};
    recs.forEach((r) => { next[r.id] = !allOn; });
    setSelected((prev) => ({ ...prev, [entityType]: next }));
  };

  const handlePullSelected = async () => {
    // Gather all (entityType, rec) pairs that are checked
    const tasks = [];
    Object.entries(selected).forEach(([entityType, ids]) => {
      Object.entries(ids).forEach(([id, checked]) => {
        if (checked) {
          const rec = (records[entityType] || []).find((r) => r.id === id);
          if (rec) tasks.push({ entityType, rec });
        }
      });
    });
    if (!tasks.length) return;

    setPulling(true);
    setPullProgress({ done: 0, total: tasks.length });

    let successCount = 0;
    let errorCount   = 0;

    for (const { entityType, rec } of tasks) {
      try {
        const res = await fetch(
          `${API}/api/pursuit-kb/${pursuitId}/documents/pull-from-kb`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ entity_type: entityType, entity_id: rec.id, include_attachments: true }),
          },
        );
        if (res.ok) successCount += 1;
        else errorCount += 1;
      } catch {
        errorCount += 1;
      }
      setPullProgress((p) => ({ ...p, done: p.done + 1 }));
    }

    if (successCount > 0)
      toast.success(`Pulled ${successCount} item${successCount > 1 ? 's' : ''} into the pursuit`);
    if (errorCount > 0)
      toast.error(`${errorCount} item${errorCount > 1 ? 's' : ''} failed to pull`);

    setPulling(false);
    onPulled?.();
    if (errorCount === 0) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Key layout fixes vs. previous version:
        • flex flex-col  — overrides shadcn's default "grid" so we can use flex stretching
        • overflow-hidden — clips any child that would bleed outside the dialog border
        • gap-0 / p-0    — removes shadcn defaults so we control all spacing ourselves
        • maxHeight      — bounds total height so the list area scrolls inside, footer stays visible
        • sm:max-w-2xl   — explicitly overrides shadcn's responsive sm:max-w-lg at sm+ breakpoints
      */}
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-2xl sm:max-w-2xl flex flex-col overflow-hidden gap-0 p-0"
        style={{ maxHeight: 'min(90vh, 620px)' }}
        data-testid="pull-from-library-modal"
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="shrink-0 border-b border-slate-100 px-6 pt-5 pb-4 pr-12">
          <div className="flex items-center gap-2">
            <ArrowDownToLine className="w-5 h-5 text-amber-600 shrink-0" />
            <h2 className="text-base font-semibold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pull from Client Library
            </h2>
          </div>
          <p className="text-xs text-slate-500 mt-1 ml-7">
            Check items across any tab, then click <strong>Pull Selected</strong> to bring them into this pursuit.
          </p>
        </div>

        {/* ── Tabs body — fills remaining height, scrolls inside ── */}
        <Tabs
          value={activeType}
          onValueChange={setActiveType}
          className="flex-1 flex flex-col min-h-0 overflow-hidden px-6 pt-3 pb-1"
        >
          {/* Tab strip */}
          <TabsList className="grid grid-cols-4 w-full shrink-0">
            {ENTITY_TYPES.map((t) => {
              const Ic  = t.icon;
              const cnt = selectedForTab(t.value);
              return (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  className="relative flex items-center gap-1.5 px-2 text-xs"
                  data-testid={`pull-tab-${t.value}`}
                >
                  <Ic className={`w-3.5 h-3.5 shrink-0 ${t.color}`} />
                  <span className="truncate">{t.shortLabel}</span>
                  {cnt > 0 && (
                    <span className="absolute -top-1.5 -right-1 min-w-[17px] h-[17px] px-0.5 rounded-full bg-amber-600 text-white text-[9px] font-bold flex items-center justify-center leading-none z-10">
                      {cnt}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Per-tab content — each tab scrolls its own record list */}
          {ENTITY_TYPES.map((t) => {
            const recs     = records[t.value] || [];
            const cur      = selected[t.value] || {};
            const allOn    = recs.length > 0 && recs.every((r) => cur[r.id]);
            const someOn   = recs.some((r) => cur[r.id]);
            const isActive = activeType === t.value;

            return (
              <TabsContent
                key={t.value}
                value={t.value}
                className="flex flex-col flex-1 min-h-0 mt-3 outline-none"
              >
                {/* Select-all toolbar */}
                {!loading && recs.length > 0 && (
                  <div className="flex items-center justify-between mb-2 px-1 shrink-0">
                    <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={allOn}
                        ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                        onChange={() => toggleAll(t.value)}
                        className="rounded accent-amber-600"
                      />
                      Select all ({recs.length})
                    </label>
                    <span className="text-xs text-slate-400">
                      {selectedForTab(t.value)} selected
                    </span>
                  </div>
                )}

                {/* Scrollable record list — this is the only part that scrolls */}
                <div className="flex-1 overflow-y-auto min-h-0 space-y-1.5 pr-1">
                  {loading && isActive ? (
                    <div className="flex items-center justify-center py-10 text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                    </div>
                  ) : recs.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-10">
                      No {t.label.toLowerCase()} on this client.
                      <br />
                      <span className="text-xs text-slate-400">Add them in the Client Profile first.</span>
                    </p>
                  ) : (
                    recs.map((rec) => (
                      <label
                        key={rec.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer select-none transition-colors ${
                          cur[rec.id]
                            ? 'border-amber-300 bg-amber-50'
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                        data-testid={`pull-record-${t.value}-${rec.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!cur[rec.id]}
                          onChange={() => toggleOne(t.value, rec.id)}
                          className="shrink-0 rounded accent-amber-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {labelOf(t.value, rec)}
                          </p>
                          {subOf(t.value, rec) && (
                            <p className="text-xs text-slate-500 truncate">{subOf(t.value, rec)}</p>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>

        {/* Progress bar — shown only while batch-pulling */}
        {pulling && (
          <div className="shrink-0 px-6 pb-1">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Pulling items…</span>
              <span>{pullProgress.done} / {pullProgress.total}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-200"
                style={{ width: `${pullProgress.total ? (pullProgress.done / pullProgress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* ── Footer — always pinned at the bottom, separated by a border ── */}
        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-4 flex items-center justify-between gap-4">
          {/* Left: selection summary */}
          <p className="text-sm text-slate-500 truncate">
            {totalSelected > 0 ? (
              <span className="font-semibold text-amber-700">
                {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
              </span>
            ) : (
              'Check items above to select'
            )}
          </p>
          {/* Right: action buttons — always visible */}
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={pulling}
            >
              Close
            </Button>
            <Button
              size="sm"
              onClick={handlePullSelected}
              disabled={pulling || totalSelected === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white font-medium"
              data-testid="pull-selected-btn"
            >
              {pulling ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Pulling…</>
              ) : (
                <>
                  <ArrowDownToLine className="w-4 h-4 mr-1" />
                  Pull Selected{totalSelected > 0 ? ` (${totalSelected})` : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Promote (Push) Modal
// ---------------------------------------------------------------------------

export function PromoteToLibraryModal({
  open,
  onOpenChange,
  pursuitId,
  pursuitDocId,
  pursuitDocName,
  organizationId,
  authToken,
  defaultEntityType = 'past-performances',
  onPromoted,
}) {
  const [entityType, setEntityType] = useState(defaultEntityType);
  const [mode, setMode] = useState('attach'); // 'attach' | 'create'
  const [records, setRecords] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [createForm, setCreateForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEntityType(defaultEntityType);
    setMode('attach');
    setSelectedId('');
    setCreateForm({});
  }, [open, defaultEntityType]);

  useEffect(() => {
    const load = async () => {
      if (!open || mode !== 'attach') return;
      setLoading(true);
      try {
        const url = pursuitId
          ? `${API}/api/pursuit-kb/${pursuitId}/library/${entityType}`
          : `${API}/api/knowledge-base/${entityType}?organization_id=${organizationId}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRecords(data.items || []);
          setSelectedId('');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, entityType, mode, pursuitId, organizationId, authToken]);

  const renderCreateFields = () => {
    const f = createForm;
    const set = (k, v) => setCreateForm({ ...createForm, [k]: v });
    if (entityType === 'past-performances') {
      return (
        <>
          <div>
            <Label>Contract Title *</Label>
            <Input value={f.contract_title || ''} onChange={(e) => set('contract_title', e.target.value)} placeholder="Cloud migration support" />
          </div>
          <div>
            <Label>Client Agency *</Label>
            <Input value={f.client_agency || ''} onChange={(e) => set('client_agency', e.target.value)} placeholder="Department of Defense" />
          </div>
          <div>
            <Label>Contract Value (USD)</Label>
            <Input type="number" value={f.contract_value || ''} onChange={(e) => set('contract_value', e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
        </>
      );
    }
    if (entityType === 'certifications') {
      return (
        <>
          <div>
            <Label>Certification Name *</Label>
            <Input value={f.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="ISO 9001:2015" />
          </div>
          <div>
            <Label>Issuing Body</Label>
            <Input value={f.issuing_body || ''} onChange={(e) => set('issuing_body', e.target.value)} />
          </div>
          <div>
            <Label>Category</Label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              value={f.category || 'QUALITY'}
              onChange={(e) => set('category', e.target.value)}
            >
              <option value="QUALITY">QUALITY</option>
              <option value="SECURITY">SECURITY</option>
              <option value="SMALL_BUSINESS">SMALL BUSINESS</option>
              <option value="TECHNICAL">TECHNICAL</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>
        </>
      );
    }
    if (entityType === 'key-personnel') {
      return (
        <>
          <div>
            <Label>First Name *</Label>
            <Input value={f.first_name || ''} onChange={(e) => set('first_name', e.target.value)} />
          </div>
          <div>
            <Label>Last Name *</Label>
            <Input value={f.last_name || ''} onChange={(e) => set('last_name', e.target.value)} />
          </div>
          <div>
            <Label>Title</Label>
            <Input value={f.title || ''} onChange={(e) => set('title', e.target.value)} placeholder="Sr. Program Manager" />
          </div>
        </>
      );
    }
    if (entityType === 'winning-proposals') {
      return (
        <>
          <div>
            <Label>Proposal Title *</Label>
            <Input value={f.title || ''} onChange={(e) => set('title', e.target.value)} />
          </div>
          <div>
            <Label>Agency</Label>
            <Input value={f.agency || ''} onChange={(e) => set('agency', e.target.value)} />
          </div>
          <div>
            <Label>Contract Value (USD)</Label>
            <Input type="number" value={f.contract_value || ''} onChange={(e) => set('contract_value', e.target.value === '' ? '' : Number(e.target.value))} />
          </div>
        </>
      );
    }
    return null;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { entity_type: entityType };
      if (mode === 'attach') {
        if (!selectedId) {
          toast.error('Pick a record to attach to');
          setSaving(false);
          return;
        }
        payload.entity_id = selectedId;
      } else {
        // Strip empty values from createForm.
        const cleaned = {};
        Object.entries(createForm).forEach(([k, v]) => {
          if (v !== '' && v !== undefined && v !== null) cleaned[k] = v;
        });
        payload.create_new = cleaned;
      }

      const res = await fetch(
        `${API}/api/pursuit-kb/${pursuitId}/documents/${pursuitDocId}/promote-to-kb`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Save failed');
      }
      const result = await res.json();
      toast.success(
        result.created_new_record
          ? 'Saved to library as a new record'
          : 'Saved to library record',
      );
      onPromoted?.(result);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]" data-testid="promote-to-library-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ArrowUpToLine className="w-5 h-5 text-blue-600" />
            Save to Client Library
          </DialogTitle>
          {pursuitDocName && (
            <p className="text-xs text-slate-500 mt-1">
              File: <span className="font-medium text-slate-700">{pursuitDocName}</span>
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Target card</Label>
            <div className="flex gap-2 mt-1.5 flex-wrap">
              {ENTITY_TYPES.map((t) => {
                const Ic = t.icon;
                const active = entityType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setEntityType(t.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`promote-target-${t.value}`}
                  >
                    <Ic className="w-3.5 h-3.5" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Action</Label>
            <div className="flex gap-2 mt-1.5">
              <button
                type="button"
                onClick={() => setMode('attach')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
                  mode === 'attach' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-slate-600 border-slate-200'
                }`}
                data-testid="promote-mode-attach"
              >
                Attach to existing
              </button>
              <button
                type="button"
                onClick={() => setMode('create')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
                  mode === 'create' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-slate-600 border-slate-200'
                }`}
                data-testid="promote-mode-create"
              >
                <Plus className="w-3 h-3 inline mr-1" />Create new record
              </button>
            </div>
          </div>

          {mode === 'attach' ? (
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Existing records</Label>
              <div className="mt-1.5 max-h-60 overflow-y-auto space-y-1 border border-slate-200 rounded-md p-1.5">
                {loading ? (
                  <div className="py-4 text-center text-sm text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
                  </div>
                ) : records.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-4">
                    No records — switch to <strong>Create new</strong>.
                  </p>
                ) : (
                  records.map((r) => (
                    <label
                      key={r.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${
                        selectedId === r.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`promote-existing-${r.id}`}
                    >
                      <input
                        type="radio"
                        name="promote-target"
                        checked={selectedId === r.id}
                        onChange={() => setSelectedId(r.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{labelOf(entityType, r)}</p>
                        {subOf(entityType, r) && (
                          <p className="text-xs text-slate-500 truncate">{subOf(entityType, r)}</p>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {renderCreateFields()}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="promote-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : 'Save to Library'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Win→Library Auto-Prompt Dialog
// ---------------------------------------------------------------------------

export function WinLibraryPromptDialog({
  open,
  onOpenChange,
  pursuitId,
  organizationId,
  authToken,
  onCompleted,
}) {
  const [candidates, setCandidates] = useState([]);
  const [suggested, setSuggested] = useState(null);
  const [selectedIds, setSelectedIds] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposalForm, setProposalForm] = useState({});

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/api/pursuit-kb/${pursuitId}/win-library/candidates`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (res.ok) {
          const data = await res.json();
          setCandidates(data.candidates || []);
          setSuggested(data.suggested_winning_proposal || null);
          setProposalForm({
            title: data.suggested_winning_proposal?.title || '',
            agency: data.suggested_winning_proposal?.agency || '',
            contract_value: data.suggested_winning_proposal?.contract_value || '',
            naics_code: data.suggested_winning_proposal?.naics_code || '',
            award_date: data.suggested_winning_proposal?.award_date
              ? data.suggested_winning_proposal.award_date.slice(0, 10)
              : '',
            source_pursuit_id: pursuitId,
          });
          // Pre-select all candidates by default.
          const initial = {};
          (data.candidates || []).forEach((c) => { initial[c.id] = true; });
          setSelectedIds(initial);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, pursuitId, authToken]);

  const handlePromote = async () => {
    setSaving(true);
    try {
      // Step 1 — create the Winning Proposal record (carries the metadata).
      const wpPayload = { ...proposalForm, organization_id: organizationId };
      Object.keys(wpPayload).forEach((k) => {
        if (wpPayload[k] === '' || wpPayload[k] === undefined) delete wpPayload[k];
      });
      const wpRes = await fetch(`${API}/api/knowledge-base/winning-proposals`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(wpPayload),
      });
      if (!wpRes.ok) {
        const err = await wpRes.json().catch(() => ({}));
        throw new Error(err?.detail || 'Failed to create Winning Proposal');
      }
      const wp = await wpRes.json();

      // Step 2 — promote each selected pursuit doc as an attachment.
      const docsToPromote = Object.entries(selectedIds)
        .filter(([, v]) => v)
        .map(([id]) => id);

      let attached = 0;
      for (const docId of docsToPromote) {
        const r = await fetch(
          `${API}/api/pursuit-kb/${pursuitId}/documents/${docId}/promote-to-kb`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              entity_type: 'winning-proposals',
              entity_id: wp.id,
            }),
          },
        );
        if (r.ok) attached += 1;
      }
      toast.success(
        `Winning Proposal saved to Client Library${attached > 0 ? ` with ${attached} file${attached > 1 ? 's' : ''}` : ''}`,
      );
      onCompleted?.(wp);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Promote failed');
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]" data-testid="win-library-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Trophy className="w-5 h-5 text-amber-600" />
            Promote to Winning Proposals
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Save this win to the Client Library so future pursuits can reuse the proposal package.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Proposal Title *</Label>
              <Input
                value={proposalForm.title || ''}
                onChange={(e) => setProposalForm({ ...proposalForm, title: e.target.value })}
                data-testid="winlib-title-input"
              />
            </div>
            <div>
              <Label>Agency</Label>
              <Input
                value={proposalForm.agency || ''}
                onChange={(e) => setProposalForm({ ...proposalForm, agency: e.target.value })}
              />
            </div>
            <div>
              <Label>Contract Value (USD)</Label>
              <Input
                type="number"
                value={proposalForm.contract_value || ''}
                onChange={(e) => setProposalForm({
                  ...proposalForm,
                  contract_value: e.target.value === '' ? '' : Number(e.target.value),
                })}
              />
            </div>
            <div>
              <Label>NAICS Code</Label>
              <Input
                value={proposalForm.naics_code || ''}
                onChange={(e) => setProposalForm({ ...proposalForm, naics_code: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Award Date</Label>
              <Input
                type="date"
                value={proposalForm.award_date || ''}
                onChange={(e) => setProposalForm({ ...proposalForm, award_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">
              Files to attach ({selectedCount} selected)
            </Label>
            <div className="mt-1.5 max-h-52 overflow-y-auto border border-slate-200 rounded-md p-1.5">
              {loading ? (
                <div className="py-4 text-center text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
                </div>
              ) : candidates.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">
                  No promotable files found in this pursuit.
                </p>
              ) : (
                candidates.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 text-sm"
                    data-testid={`winlib-cand-${c.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedIds[c.id]}
                      onChange={(e) => setSelectedIds({ ...selectedIds, [c.id]: e.target.checked })}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{c.file_name}</p>
                      {c.group_name && (
                        <Badge variant="outline" className="text-[10px] mr-1">{c.group_name}</Badge>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Skip
          </Button>
          <Button
            onClick={handlePromote}
            disabled={saving || !proposalForm.title?.trim()}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="winlib-promote-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Promoting…</> : 'Save to Client Library'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
